/**
 * ArrowLayer - Renders mechanism arrows from SVG generation data
 *
 * Consumes arrow data from SVGGenerationContext and renders them as SVG.
 * Default visual rendering uses DynamicArrow (motion-enabled).
 * No hitboxes — arrows use their own interaction model via ArrowDrawingProvider.
 */

import React, { memo } from 'react';
import { useSVGGenerationData } from '../../contexts/SVGGenerationContext';
import { DynamicArrow } from '../../motion/components/DynamicArrow';
import type { ArrowData, ElectronData, ChargeData } from '../../rendering/useSVGGeneration';

export interface ArrowLayerProps {
  /** Custom render function for each arrow. Defaults to DynamicArrow. */
  render?: (
    arrowData: ArrowData,
    allElectronData: ElectronData[],
    allChargeData: ChargeData[],
  ) => React.ReactNode;
}

// React.memo (PIPH-69): see AtomLayer for rationale.
export const ArrowLayer = memo(function ArrowLayer({ render }: ArrowLayerProps) {
  const { arrowData, electronData, chargeData } = useSVGGenerationData();

  return (
    <g data-layer="arrows">
      {arrowData.map((arrow) => (
        <React.Fragment key={`arrow-${arrow.index}`}>
          {render
            ? render(arrow, electronData, chargeData)
            : <DynamicArrow arrowData={arrow} allElectronData={electronData} allChargeData={chargeData} />
          }
        </React.Fragment>
      ))}
    </g>
  );
});
